import { flow } from 'lodash/fp';

// Modified regexp from https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
const LINK_REGEXP = /(http|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/gi;
const EMAIL_REGEXP =
  /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/gi;

// parse and insert links
const linkify = (text: string): string => {
  const MARKDOWN_LINK_REGEXP = /\[(.*?)\]\((.*?)\)/g;

  // Check markdown syntax first or replace all links
  if (text.match(MARKDOWN_LINK_REGEXP)) {
    return text.replace(MARKDOWN_LINK_REGEXP, (_, anchor, url) => {
      const isInternal = url?.includes(window.location.host);
      return `<a href="${url.trim()}" target="_blank"${
        !isInternal ? ' rel="nofollow noopener noreferrer"' : ''
      }>${anchor.trim()}</a>`;
    });
  } else {
    return text.replace(LINK_REGEXP, url => {
      const isInternal = url?.includes(window.location.host);
      return `<a href="${url.trim()}" target="_blank"${
        !isInternal ? ' rel="nofollow noopener noreferrer"' : ''
      }>${url.trim()}</a>`;
    });
  }
};

const insertMention = (text: string): string => {
  const host = window.location.origin;
  return text.replace(/(^|\s)(@(?!_)[a-z0-9_\-\.]+[^\., !?:;"])/gi, (match, space, username) => {
    const profile = match.trim();
    return `${space}<a href="${host}/war-room/profile/${username.trim().slice(1)}" target="_blank">${profile}</a>`;
  });
};

const insertEmail = (text: string): string => {
  return text.replace(EMAIL_REGEXP, email => `<a href="mailto:${email}">${email}</a>`);
};

const getListType = (text: string | null): string | null => {
  if (!text) return null;
  const isOlList = text.startsWith('<ol>');
  const isUlList = text.startsWith('<ul>');
  return isOlList ? 'ol' : isUlList ? 'ul' : null;
};

// Wrap li tags in a row to <ul> or <ol> tags, remove excess ol/ul tags inside chunks
const transformLists = (chunks: string[]): string[] => {
  let indexInTheList: number | null = null;

  return chunks.reduce((acc: string[], chunk: string, index) => {
    if (getListType(chunk)) {
      indexInTheList === null ? (indexInTheList = 0) : indexInTheList++;
      // TODO: case when different types of lists are mixed
      const isNextChunkInList = Boolean(getListType(chunks[index + 1]));

      // Remove closing ol/li from first chunk in the list
      if (indexInTheList === 0 && isNextChunkInList) {
        chunk = chunk.replaceAll(/<\/(ol|ul)>$/g, '');
        acc.push(chunk);
        return acc;
      }

      // Remove opening and closing ol/ul from chunk in the middle
      // or for only one chunk in the list
      if (indexInTheList > 0 && isNextChunkInList) {
        chunk = chunk.replaceAll(/^<(ol|ul)>|<\/(ol|ul)>$/g, '');
        acc.push(chunk);
        return acc;
      }

      // Remove opening ol/ul from last chunk in the list
      if (indexInTheList > 0 && !isNextChunkInList) {
        chunk = chunk.replaceAll(/^<(ol|ul)>/g, '');
        acc.push(chunk);
        indexInTheList = null;
        return acc;
      }

      // Keep everything as is if it's only one chunk in the list
      acc.push(chunk);
      indexInTheList = null;
      return acc;
    }

    acc.push(chunk);
    return acc;
  }, []);
};

const transformIntoParagrapsAndListItems = (chunks: string[]): string[] => {
  return chunks.map(chunk => {
    const listMarker = chunk.slice(0, 3);
    const textWithoutMarker = chunk.slice(2).replace(/^\./i, '').trim();

    // We wrap every li to ol/ul to understand which list is it in next steps and then remove excess tags
    // TODO: add support for nested lists
    if (['- ', '* '].some(marker => listMarker.startsWith(marker))) {
      return `<ul><li>${textWithoutMarker}</li></ul>`;
    }
    // Starts with `1. / 1)` or `2. / 2)` etc
    if (listMarker.match(/^\d+(\.|\))/)) {
      return `<ol><li>${textWithoutMarker}</li></ol>`;
    }
    return `<p>${chunk}</p>`;
  });
};

const breakIntoChunks = (text: string): string[] => {
  return text
    .split(/(\r\n|\n\r|\r|\n|^|$)/g)
    .map(chunk => chunk.replace(/(\r|\n)/g, '').trim())
    .filter(Boolean);
};

const insertLinks = (chunks: string[]): string[] => {
  return chunks.map(chunk => flow([linkify, insertMention, insertEmail])(chunk));
};

// 1. Break down text chunks by line breaks, start and end of text
// 2. Process each chunk individually
//   - build list items
//   - wrap paragraphs
//   - insert links
// NOTE: if you add new transformation - change tests!
export default (markup: string) => {
  const chunks = breakIntoChunks(flow([addLineBreaks, transformHtmlEntities, fixSymbols, transformMarkdown])(markup));
  return flow([insertLinks, transformIntoParagrapsAndListItems, transformLists])(chunks).join('');
};

// Break down the text by line breaks if text has list items inside
export const addLineBreaks = (text: string): string => {
  if (text.includes('\n')) return text;
  // any sentense ending + "- , * , 1. , 1)" after
  const listItemRegexp = /(\S(?:\.|:|;|!|\?|,|")\s)(- |\* |\d+(\.|\)))/gi;
  const listItems = [...text.matchAll(listItemRegexp)];
  const hasListItems = listItems?.length > 2;
  if (hasListItems) {
    let textArray = text.replace(listItemRegexp, '$1\n$2').split('\n');
    // Put line break after sentence end (.;:) of last item
    textArray[textArray.length - 1] = textArray[textArray.length - 1].replace(/(\S(\.|:|;|!|\?)\s)/i, '$1\n');
    return textArray.join('\n');
  }

  return text;
};

export const fixSymbols = (text: string): string => {
  // Case -- -> —
  return text.replace(/--/g, '—');
};

export const transformMarkdown = (text: string): string => {
  // Transform **bold** to <strong>bold</strong>
  // Transform *italic* to <em>italic</em>
  return text.replace(/\*\*(.*?)\*\*/gm, '<strong>$1</strong>').replace(/\*(.*?)\*/gm, '<em>$1</em>');
};

const transformHtmlEntities = (text: string): string => {
  return text.replace(/&/gm, '&amp;').replace(/</gm, '&lt;').replace(/>/gm, '&gt;');
};

/**
 * Just a temporary solution to remove tags from current salesGPT markup
 * as we can't test on development environment
 * remove p, ul, ol, li tags
 */
export const removeTags = (text: string): string => {
  return (
    text
      .replace(/<p>/gm, '\n')
      .replace(/<\/p>/gm, '\n')
      // Replace <ul> <ol> <li> with needed symbols
      .replace(/<(ol|ul)>(.)*?<\/(ol|ul)>/gms, (match, listTag) => {
        const listItemText = listTag === 'ul' ? '- ' : '1. ';

        return match.replace(/<li>((.)*?)<\/li>/gms, (_, itemText) => {
          return `${listItemText}${itemText}\n`;
        });
      })
      .replace(/<(ul|ol)>/gm, '\n')
      .replace(/<\/ul>/gm, '\n')
      .replace(/<\/ol>/gm, '\n')
      .replace(/<a\b[^>]*>/gim, '')
      .replace(/<\/a>/gi, '')
      .replace(/<strong>/g, '**')
      .replace(/<\/strong>/g, '**')
      .replace(/<em>/gm, '*')
      .replace(/<\/em>/gm, '*')
      .replace(/&amp;/gm, '&')
      .replace(/&lt;/gm, '<')
      .replace(/&gt;/gm, '>')
  );
};
